import React, { useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  textArea: {
    minHeight: "200px",
    fontFamily: "monospace",
  },
  statsBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  numberPreview: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    maxHeight: "150px",
    overflowY: "auto",
  },
}));

const normalizePhoneNumber = (phone) => {
  return (phone || "").replace(/\D/g, "");
};

const validatePhoneNumber = (phone) => {
  const normalized = normalizePhoneNumber(phone);
  return normalized.length >= 10 && normalized.length <= 15;
};

const parsePhoneNumbers = (text) => {
  if (!text || !text.trim()) return { numbers: [], invalid: [] };

  const separators = /[\n\r,;]+/;
  const lines = text.split(separators);
  const numbers = [];
  const invalid = [];

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const numberMatch = trimmed.match(/(\d[\d\s\-\(\)]+)/);
    if (numberMatch) {
      const numberStr = numberMatch[1];
      if (validatePhoneNumber(numberStr)) {
        numbers.push({
          original: trimmed,
          number: normalizePhoneNumber(numberStr),
          name: trimmed.replace(numberStr, "").trim() || null,
        });
      } else {
        invalid.push(trimmed);
      }
    } else {
      if (validatePhoneNumber(trimmed)) {
        numbers.push({
          original: trimmed,
          number: normalizePhoneNumber(trimmed),
          name: null,
        });
      } else {
        invalid.push(trimmed);
      }
    }
  });

  return { numbers, invalid };
};

const ManualAddContactsModal = ({
  open,
  onClose,
  contactListProId,
  onSuccess,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [contactLists, setContactLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(contactListProId || "");
  const [textInput, setTextInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [parsedData, setParsedData] = useState({ numbers: [], invalid: [] });

  useEffect(() => {
    if (open && !contactListProId) {
      api.get("/contact-lists-pro/list", { params: { companyId: user?.companyId } })
        .then(({ data }) => setContactLists(Array.isArray(data) ? data : []))
        .catch(() => setContactLists([]));
    }
  }, [open, contactListProId, user?.companyId]);

  useEffect(() => {
    if (open) {
      setSelectedListId(contactListProId || "");
    }
  }, [open, contactListProId]);

  const handleTextChange = (e) => {
    const value = e.target.value;
    setTextInput(value);
    setParsedData(value ? parsePhoneNumbers(value) : { numbers: [], invalid: [] });
  };

  const listId = contactListProId || selectedListId;

  const handleAddContacts = async () => {
    if (!listId) {
      toast.error("Selecione uma lista de contatos!");
      return;
    }
    if (parsedData.numbers.length === 0) {
      toast.error("Nenhum número válido encontrado!");
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    let duplicateCount = 0;

    try {
      const batchSize = 50;
      for (let i = 0; i < parsedData.numbers.length; i += batchSize) {
        const batch = parsedData.numbers.slice(i, i + batchSize);
        const promises = batch.map(async (contact) => {
          try {
            await api.post(`/contact-lists-pro/${listId}/items`, {
              name: contact.name,
              number: contact.number,
              optedIn: true,
            });
            successCount++;
          } catch (err) {
            if (err.response?.status === 400 && (err.response?.data?.message || "").toLowerCase().includes("duplicat")) {
              duplicateCount++;
            } else {
              errorCount++;
            }
          }
        });
        await Promise.all(promises);
      }

      let message = `${successCount} contato(s) adicionado(s) com sucesso!`;
      if (duplicateCount > 0) message += ` ${duplicateCount} duplicado(s) ignorado(s).`;
      if (errorCount > 0) message += ` ${errorCount} erro(s).`;
      toast.success(message);

      if (onSuccess) onSuccess();
      setTextInput("");
      setParsedData({ numbers: [], invalid: [] });
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Adicionar Contatos Manualmente</DialogTitle>
      <DialogContent>
        {!contactListProId && (
          <FormControl fullWidth variant="outlined" margin="normal" size="small">
            <InputLabel>Lista de contatos</InputLabel>
            <Select
              value={selectedListId}
              onChange={(e) => setSelectedListId(e.target.value)}
              label="Lista de contatos"
            >
              <MenuItem value="">Selecione uma lista...</MenuItem>
              {contactLists.map((list) => (
                <MenuItem key={list.id} value={list.id}>
                  {list.name} ({(list.contactsCount ?? 0)} contatos)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Typography variant="body2" color="textSecondary" gutterBottom style={{ marginTop: 8 }}>
          Cole os números de telefone abaixo. Separe por linha, vírgula ou ponto-e-vírgula.
          <br />
          Exemplo: 5511999999999 ou João 5511999999999
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={8}
          variant="outlined"
          placeholder="Cole os números aqui..."
          value={textInput}
          onChange={handleTextChange}
          className={classes.textArea}
          disabled={loading || !listId}
          margin="normal"
        />

        {textInput && (
          <Box className={classes.statsBox}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary">Válidos:</Typography>
                <Chip label={parsedData.numbers.length} color="primary" size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary">Inválidos:</Typography>
                <Chip
                  label={parsedData.invalid.length}
                  color={parsedData.invalid.length > 0 ? "secondary" : "default"}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary">Total:</Typography>
                <Chip label={parsedData.numbers.length + parsedData.invalid.length} size="small" />
              </Grid>
            </Grid>
            {parsedData.numbers.length > 0 && (
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Preview (máx. 20):
                </Typography>
                <Box className={classes.numberPreview}>
                  {parsedData.numbers.slice(0, 20).map((contact, idx) => (
                    <Chip
                      key={idx}
                      label={contact.name ? `${contact.name}: ${contact.number}` : contact.number}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                  {parsedData.numbers.length > 20 && (
                    <Chip label={`+${parsedData.numbers.length - 20} mais...`} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          onClick={handleAddContacts}
          color="primary"
          variant="contained"
          disabled={loading || parsedData.numbers.length === 0 || !listId}
        >
          {loading ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              Adicionando...
            </>
          ) : (
            `Adicionar ${parsedData.numbers.length} Contato(s)`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualAddContactsModal;
